<template>
  <BaseTemplate app-name="Perfil" page-title="">
    <template #content>
      <div class="container mx-auto px-4 py-8 max-w-5xl">
        <!-- Avatar Section -->
        <div class="flex justify-center mb-8">
          <div class="relative inline-block">
            <div class="h-48 w-48 overflow-hidden rounded-full border-4 border-white bg-white shadow-lg dark:border-gray-800 sm:h-64 sm:w-64 lg:h-72 lg:w-72">
              <!-- Enquanto loadingProfile for true, exibe spinner; caso contrário, a imagem -->
              <template v-if="loadingProfile">
                <div class="h-full w-full animate-spin rounded-full border-[10px] border-black/10 border-t-blue-500 dark:border-white/10 dark:border-t-blue-400" role="status" aria-label="Carregando"></div>
              </template>
              <template v-else>
                <img
                  :src="profileImage || avatarPlaceholder"
                  alt="Avatar"
                  class="h-full w-full object-cover transition-transform duration-300"
                  @error="handleImageError"
                />
              </template>
            </div>
            <button
              @click="triggerFileInput"
              class="absolute bottom-2 right-2 rounded-full bg-blue-500 p-2.5 text-white shadow-md transition hover:scale-110 hover:bg-blue-600 sm:p-3"
              aria-label="Alterar imagem de perfil"
            >
              <Camera class="w-5 h-5 sm:w-10 sm:h-10" />
            </button>
            <input
              type="file"
              ref="fileInputRef"
              @change="handleImageUpload"
              accept="image/*"
              class="hidden"
            />
          </div>
        </div>

        <!-- Profile Information Card -->
        <div class="bg-white dark:bg-gray-800 rounded-2xl shadow-xl overflow-hidden">
          <div class="p-6 md:p-8">
            <!-- Name Section -->
            <div class="mb-8">
              <div v-if="isEditingName" class="flex flex-col sm:flex-row items-start sm:items-center gap-4">
                <input
                  v-model="firstName"
                  class="w-full sm:flex-1 border-b-2 border-blue-500 px-2 py-1 text-xl md:text-2xl font-semibold focus:outline-none bg-transparent dark:text-white"
                  placeholder="Nome"
                />
                <input
                  v-model="lastName"
                  class="w-full sm:flex-1 border-b-2 border-blue-500 px-2 py-1 text-xl md:text-2xl font-semibold focus:outline-none mt-4 sm:mt-0 bg-transparent dark:text-white"
                  placeholder="Sobrenome"
                />
                <div class="flex gap-2 mt-4 sm:mt-0">
                  <button @click="cancelEditingName" class="text-gray-500 hover:text-gray-700 dark:hover:text-gray-300 p-2 transition-colors">
                    <X class="w-5 h-5" />
                  </button>
                  <button @click="saveName" class="bg-blue-500 text-white px-4 py-2 rounded-md hover:bg-blue-600 transition-colors">
                    Salvar
                  </button>
                </div>
              </div>
              <div v-else class="flex flex-col sm:flex-row sm:items-center sm:justify-between">
                <div>
                  <h2 class="text-2xl md:text-3xl font-bold text-gray-900 dark:text-white">
                    {{ `${firstName} ${lastName}` }}
                  </h2>
                </div>
                <button @click="startEditingName" class="text-blue-500 hover:text-blue-600 text-sm flex items-center gap-2 mt-2 sm:mt-0 transition-colors">
                  <Edit class="w-4 h-4" /> Editar
                </button>
              </div>
            </div>

            <!-- Profile Information -->
            <div class="space-y-6">
              <div class="space-y-2">
                <label for="email" class="block text-sm font-medium text-gray-700 dark:text-gray-300">E-mail:</label>
                <div class="flex items-center gap-3 border-b border-gray-300 dark:border-gray-600 focus-within:border-blue-500 transition-colors">
                  <Mail class="w-5 h-5 text-gray-500 dark:text-gray-400" />
                  <input 
                    id="email" 
                    v-model="email" 
                    class="w-full px-2 py-2 focus:outline-none bg-transparent dark:text-white"
                  />
                </div>
              </div>

              <div class="flex justify-end mt-8">
                <button 
                  @click="saveChanges" 
                  class="bg-gradient-to-r from-blue-500 to-blue-600 text-white px-6 py-3 rounded-lg shadow-md hover:from-blue-600 hover:to-blue-700 transition-all duration-300 transform hover:scale-105"
                >
                  Salvar Alterações
                </button>
              </div>
            </div>
          </div>
        </div>

        <!-- API Key Card (Separado) -->
        <div class="bg-white dark:bg-gray-800 rounded-2xl shadow-xl overflow-hidden mt-6">
          <div class="p-6 md:p-8">
            <h3 class="text-lg font-semibold mb-4 text-gray-900 dark:text-white">Sua API Key</h3>
            <div class="space-y-2">
              <p class="text-sm text-gray-500 dark:text-gray-400 mb-4">
                Esta é sua chave de API pessoal. Você pode visualizá-la ou copiá-la para usar em suas integrações.
              </p>
              <div class="flex items-center gap-3 p-3 bg-gray-50 dark:bg-gray-700 rounded-lg border border-gray-200 dark:border-gray-600">
                <div class="flex-1 font-mono overflow-hidden overflow-ellipsis whitespace-nowrap">
                  <span v-if="showApiKey">{{ apiKey }}</span>
                  <span v-else>{{ apiKey.replace(/./g, '•') }}</span>
                </div>
                <div class="flex items-center gap-2">
                  <button 
                    @click="toggleApiKey" 
                    class="p-2 text-gray-500 hover:text-gray-700 dark:hover:text-gray-300 focus:outline-none transition-colors"
                    :title="showApiKey ? 'Ocultar API Key' : 'Mostrar API Key'"
                  >
                    <EyeOff v-if="showApiKey" class="w-5 h-5" />
                    <Eye v-else class="w-5 h-5" />
                  </button>
                  <button 
                    @click="copyApiKey" 
                    class="p-2 text-gray-500 hover:text-gray-700 dark:hover:text-gray-300 focus:outline-none transition-colors"
                    title="Copiar API Key"
                  >
                    <Copy class="w-5 h-5" />
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- InovaGuardian Download Card -->
        <div class="bg-white dark:bg-gray-800 rounded-2xl shadow-xl overflow-hidden mt-6">
          <div class="p-6 md:p-8">
            <div class="flex items-center gap-4 mb-6">
              <img 
                src="/guardian_icon.png" 
                alt="InovaGuardian Logo" 
                class="w-12 h-12 rounded-lg shadow-md"
                @error="$event.target.src='/default-avatar.png'"
              />
              <div>
                <h3 class="text-lg font-semibold text-gray-900 dark:text-white">GuardianCCDTI</h3>
                <p class="text-sm text-gray-500 dark:text-gray-400">Monitoramento de Sistemas</p>
              </div>
            </div>
            <div class="space-y-4">
              <p class="text-sm text-gray-600 dark:text-gray-300">
                Baixe o instalador do GuardianCCDTI para monitoramento completo de sistemas Windows.
              </p>
              <button 
                @click="downloadInovaGuardian" 
                class="w-full bg-gradient-to-r from-green-500 to-green-600 text-white px-6 py-3 rounded-lg shadow-md hover:from-green-600 hover:to-green-700 transition-all duration-300 transform hover:scale-105 flex items-center justify-center gap-2 font-medium"
              >
                <Download class="w-5 h-5" />
                Baixar GuardianCCDTI
              </button>
              <div class="flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400">
                <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                </svg>
                <span>Download seguro</span>
              </div>
            </div>
          </div>
        </div>

        <!-- Modal de Recorte de Imagem -->
        <div v-if="showCropper" class="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4">
          <div class="bg-white dark:bg-gray-800 rounded-lg p-6 w-full max-w-4xl">
            <h3 class="text-lg font-semibold mb-4 text-gray-900 dark:text-white">Ajustar Imagem de Perfil</h3>
            <div class="flex justify-center mb-4">
              <div class="relative rounded-full overflow-hidden border-4 border-white dark:border-gray-700 shadow-lg" style="width: 300px; height: 300px;">
                <cropper
                  ref="cropper"
                  :src="imageToCrop"
                  :stencil-component="circleStencil"
                  :stencil-props="{
                    aspectRatio: 1,
                    movable: true,
                    resizable: true,
                    handlers: {
                      eastNorth: true,
                      westNorth: true,
                      eastSouth: true,
                      westSouth: true
                    },
                  }"
                  :canvas="{ width: 300, height: 300 }"
                  class="h-full w-full bg-gray-200 dark:bg-gray-700"
                />
              </div>
            </div>
            <div class="flex justify-end space-x-3">
              <button 
                @click="cancelCrop" 
                class="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
              >
                Cancelar
              </button>
              <button 
                @click="cropAndSaveImage" 
                class="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 transition-colors"
              >
                Salvar
              </button>
            </div>
          </div>
        </div>

        <!-- Toast de Notificação -->
        <transition name="slide-up">
          <div v-if="showToast" class="fixed bottom-4 right-4 z-50 max-w-md w-full md:w-auto">
            <div 
              class="bg-white dark:bg-gray-800 rounded-lg shadow-lg overflow-hidden flex items-center p-4 border-l-4"
              :class="{
                'border-green-500': toastType === 'success',
                'border-red-500': toastType === 'error',
                'border-yellow-500': toastType === 'warning',
                'border-blue-500': toastType === 'info'
              }"
            >
              <div class="flex-shrink-0 mr-3">
                <CheckCircle v-if="toastType === 'success'" class="w-6 h-6 text-green-500" />
                <AlertCircle v-else-if="toastType === 'error'" class="w-6 h-6 text-red-500" />
                <AlertTriangle v-else-if="toastType === 'warning'" class="w-6 h-6 text-yellow-500" />
                <Info v-else class="w-6 h-6 text-blue-500" />
              </div>
              <div class="flex-1">
                <p class="font-medium text-gray-900 dark:text-white">{{ toastMessage }}</p>
              </div>
              <button @click="closeToast" class="ml-4 text-gray-400 hover:text-gray-500 focus:outline-none">
                <X class="w-5 h-5" />
              </button>
            </div>
          </div>
        </transition>

      </div>
    </template>
  </BaseTemplate>
</template>

<script>
import { Cropper } from 'vue-advanced-cropper'
import { CircleStencil } from 'vue-advanced-cropper'
import 'vue-advanced-cropper/dist/style.css'
import apiClient from '@/services/authService'
import { Camera, Mail, X, Edit, Eye, EyeOff, Copy, CheckCircle, AlertCircle, AlertTriangle, Info, Download } from 'lucide-vue-next'
import BaseTemplate from '../templates/BaseTemplate.vue'
import { usePerfilStore } from '@/stores/settings/perfilStore' // Importa o store

export default {
  components: {
    Cropper,
    Camera,
    Mail,
    X,
    Edit,
    Eye,
    EyeOff,
    Copy,
    CheckCircle,
    AlertCircle,
    AlertTriangle,
    Info,
    Download,
    BaseTemplate
  },
  data() {
    return {
      circleStencil: CircleStencil,
      firstName: '',
      lastName: '',
      email: '',
      profileImage: '',
      // Placeholder em data URL para quando não há foto (evita requisições infinitas ao falhar /default-avatar.png)
      avatarPlaceholder: 'data:image/svg+xml,' + encodeURIComponent('<svg xmlns="http://www.w3.org/2000/svg" width="200" height="200" viewBox="0 0 200 200"><rect fill="%23e5e7eb" width="200" height="200"/><text x="50%" y="50%" dominant-baseline="middle" text-anchor="middle" fill="%239ca3af" font-size="80" font-family="sans-serif">?</text></svg>'),
      isEditingName: false,
      showCropper: false,
      imageToCrop: '',
      fileInputRef: null,
      loadingProfile: true, // Adiciona flag de loading para o perfil
      apiKey: '', // Adiciona a propriedade para a apiKey
      showApiKey: false, // Controle do olho mágico
      showToast: false, // Controle de exibição do toast
      toastMessage: '', // Mensagem do toast
      toastType: 'success', // Tipo do toast (success, error, warning, info)
      toastTimeout: null, // Timeout para fechar o toast automaticamente
    }
  },
  methods: {
    triggerFileInput() {
      this.$refs.fileInputRef.click()
    },
    handleImageUpload(event) {
      const file = event.target.files?.[0]
      if (file) {
        if (file.size > 1024 * 1024 * 5) {
          this.showToastMessage("Imagem muito grande. Máximo 5MB.", "error")
          return
        }
        const reader = new FileReader()
        reader.onload = (e) => {
          if (e.target?.result) {
            this.imageToCrop = e.target.result
            this.showCropper = true
          }
        }
        reader.readAsDataURL(file)
      }
    },
    copyApiKey() {
      if (!this.apiKey) return
      navigator.clipboard.writeText(this.apiKey).then(() => {
        this.showToastMessage("API Key copiada com sucesso!")
      }).catch(err => {
        console.error("Erro ao copiar API Key:", err)
        this.showToastMessage("Erro ao copiar API Key.", "error")
      })
    },
    showToastMessage(message, type = 'success') {
      // Limpa qualquer timeout existente
      if (this.toastTimeout) {
        clearTimeout(this.toastTimeout)
      }
      
      // Define a mensagem, o tipo e mostra o toast
      this.toastMessage = message
      this.toastType = type // Agora estamos usando o parâmetro type
      this.showToast = true
      
      // Define um timeout para fechar o toast automaticamente após 3 segundos
      this.toastTimeout = setTimeout(() => {
        this.showToast = false
      }, 3000)
    },
    closeToast() {
      this.showToast = false
      if (this.toastTimeout) {
        clearTimeout(this.toastTimeout)
      }
    },
    cropAndSaveImage() {
      const cropper = this.$refs.cropper
      if (cropper) {
        const result = cropper.getResult({
          size: { width: 300, height: 300 },
          fillColor: 'rgba(0, 0, 0, 0)'
        })
        if (result && result.canvas) {
          const ctx = result.canvas.getContext('2d')
          ctx.clearRect(0, 0, result.canvas.width, result.canvas.height)
          this.profileImage = result.canvas.toDataURL('image/png')
          this.showCropper = false
          this.saveChanges()
        }
      }
    },
    cancelCrop() {
      this.showCropper = false
    },
    startEditingName() {
      this.isEditingName = true
    },
    cancelEditingName() {
      this.isEditingName = false
    },
    saveName() {
      this.isEditingName = false
    },
    toggleApiKey() {
      this.showApiKey = !this.showApiKey
    },
    async carregarPerfil() {
      this.loadingProfile = true
      try {
        const { data } = await apiClient.get('/api/v1/profile/')
        this.firstName = data.nome
        this.lastName = data.sobrenome
        this.email = data.email
        this.apiKey = data.api_key // <-- Recebe a apiKey
        if (data.imagem_base64) {
          this.profileImage = data.imagem_base64.startsWith('http')
            ? data.imagem_base64
            : `data:image/jpeg;base64,${data.imagem_base64}`
        } else {
          this.profileImage = ''
        }
      } catch (error) {
        console.error("Erro ao carregar perfil:", error)
        this.showToastMessage("Erro ao carregar perfil.", "error")
      } finally {
        this.loadingProfile = false
      }
    },
    async saveChanges() {
      try {
        const payload = {
          nome: this.firstName,
          sobrenome: this.lastName,
          email: this.email,
          // Envia a imagem somente se for nova; caso contrário, envia string vazia para evitar erro
          imagem_base64: this.profileImage.startsWith('data:image')
            ? this.profileImage.split(',')[1]
            : ''
        }
        await apiClient.put('/api/v1/profile/', payload)
        this.showToastMessage("Perfil atualizado com sucesso!")
      } catch (error) {
        console.error("Erro ao salvar alterações:", error)
        this.showToastMessage("Erro ao salvar perfil.", "error")
      }
      // Limpa e recarrega o perfil via store
      const perfilStore = usePerfilStore()
      perfilStore.limparPerfil()
      await perfilStore.carregarPerfil()
      // Atualiza os dados locais
      this.firstName = perfilStore.firstName
      this.lastName = perfilStore.lastName
      this.email = perfilStore.email
      this.profileImage = perfilStore.profileImage
    },
    async downloadInovaGuardian() {
      try {
        // Usar o endpoint seguro do backend para obter a URL de download
        const response = await fetch('/api/v1/guardian/download-inovaguardian', {
          method: 'GET',
          credentials: 'include', // Incluir cookies de autenticação
          headers: {
            'Content-Type': 'application/json'
          }
        });
        
        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.error || 'Erro ao obter URL de download');
        }
        
        const data = await response.json();
        
        if (data.success && data.download_url) {
          // Abrir a URL presigned em nova aba para download
          window.open(data.download_url, '_blank');
          
          this.showToastMessage(data.message || 'O download do GuardianCCDTI foi iniciado', 'success');
        } else {
          throw new Error('Resposta inválida do servidor');
        }
        
      } catch (error) {
        console.error('Erro ao baixar GuardianCCDTI:', error);
        this.showToastMessage(error.message || 'Erro ao tentar baixar o GuardianCCDTI.', 'error');
      }
    },
    handleImageError() {
      // Evita loop infinito: usa placeholder local em vez de recarregar perfil
      this.profileImage = this.avatarPlaceholder
    }
  },
  computed: {
    // Computed para a imagem de perfil: se o perfil estiver carregado e a imagem for válida, retorna-a; caso contrário, fallback
    profileImageComputed() {
      return this.profileImage && (this.profileImage.startsWith('data:image') || this.profileImage.startsWith('http'))
        ? this.profileImage
        : '/default-avatar.png'
    }
  },
  mounted() {
    this.carregarPerfil()
    this.fileInputRef = this.$refs.fileInputRef
  },
  beforeUnmount() {
    // Limpa o timeout se o componente for desmontado
    if (this.toastTimeout) {
      clearTimeout(this.toastTimeout)
    }
  }
}
</script>
